import { DashboardCardProps } from '@/components/DashboardCard/localGeneric';
import { FeedbackError } from '@/components/Feedback';
import { FilterType } from '@/modules/queryfilter/domain/interfaces';
import fetchCardData from '@/services/query/fetchCardData';
import { Button, Flex } from '@chakra-ui/react';
import { Row } from '@tanstack/react-table';
import { isEmpty, orderBy } from 'lodash';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DefaultTheme, useTheme } from 'styled-components';

import {
  createGroupedHeaderAfterHeader,
  getOriginalRow,
  insertRowsAfter,
  removeRowsByKeyAfter,
} from '../utils/dataConverters';
import { serializeFiltersTabs } from './serializeFiltersTabs';

export interface SelectedItem {
  backgroundColor: string;
  cellStyle: { borderRight: string };
  color: string;
  column: string;
  dataSubcomponent: { type: string; data: unknown; title: string };
  fontSize: string;
  for_column: string;
  getCellProps: (userProps: unknown) => unknown;
  indexValue?: unknown;
  items: Array<Row<any> | unknown>;
  originalRow: Array<Row<any> | unknown>;
  render: (type: string, userProps: unknown) => unknown;
  row: {
    subComponent: string;
    product_category_name: string | null;
    local_channel: string;
    floorshare: string;
    extra_point: string;
    [key: string]: unknown;
  };
  textAlign: string;
  tooltipType: string | null;
  type: string;
  uniqueId: string;
  url: string;
  value: string;
  page?: number;
  hasMore?: boolean;
  table_filter?: string;
  filters?: Array<{ column: string; key?: string; selected: string[]; type: any }>;
  parentValues?: any[];
  selectItem?: any;
  rowData?: any;
  expandId?: string;
}
export interface TabView {
  key: string;
  label: string;
  default?: boolean;
  column: string;
  hasValue?: boolean;
  hierarchyOrder: number;
  column_info: {
    key: string;
    header: string;
    textAlign: string;
    headerTextWidth: string;
    type: string;
    columnHeaderBackgroundColor: string;
    minWidth: number;
    sortable: boolean;
  };
  selected?: SelectedItem[];
  baseURL: string;
  table_filter?: string;
}
interface ParentValue {
  [key: string]: string;
}

export type ContentTabsProps = { [key: string]: unknown } & DashboardCardProps;

interface TabsViewsContextType {
  contentTabs: ContentTabsProps;
  colors: DefaultTheme['colors'];
  tabsViews: TabView[];
  tabsViewsMap: Map<string, TabView>;
  hasTabs: boolean;
  hasActiveTabs: boolean;
  activeTabs: Map<string, TabView>;
  loading: boolean;
  contentOriginal: ContentTabsProps;
  globalLoading: boolean;
  setGlobalLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setContentOriginal: React.Dispatch<React.SetStateAction<ContentTabsProps>>;
  setActiveTabs: React.Dispatch<React.SetStateAction<Map<string, TabView>>>;
  setContentTabs: React.Dispatch<React.SetStateAction<ContentTabsProps>>;
  setCustomStart: React.Dispatch<React.SetStateAction<ContentTabsProps | null>>;
  resetInfo: () => void;
  handleSelectedItems: ({
    data,
    checked,
  }: {
    data: unknown;
    checked: boolean;
  }) => void;
  getActiveTabsAsFilters: () => string;
  fetchNextSubPage: (expandId: string) => Promise<void>;
  subPageFetching: boolean;
  subPageFetchingId: string | null;
  fetchNextMainTabPage: () => Promise<void>;
  mainTabFetching: boolean;
  mainTabHasMore: boolean;
}

interface Item {
  [key: string]: any;
}

interface Info {
  [key: string]: {
    value: any;
    tooltipType: any;
  };
}

interface TabsViewsProviderProps {
  children: ReactNode;
  value: ContentTabsProps;
}

const updateRows = (
  parentValues: ParentValue[],
  tabsViews: TabView[],
  rows: Item[],
  defaultCellStyle: { [key: string]: unknown },
  tabsViewsMap: Map<string, TabView>,
  cellClicked: { column: string; value: string; [key: string]: unknown },
  originalRow: any,
): Item[] => {
  if (parentValues?.length > 0 || tabsViews?.length > 0) {
    const rowsParsed = [] as Item[];

    rows?.forEach((row, index) => {
      const info: Info = {};

      tabsViews.forEach((tabView) => {
        info[tabView.column] = {
          ...defaultCellStyle,
          ...(row[tabView.column]?.value
            ? row[tabView.column]
            : {
                value: null,
                tooltipType: null,
              }),
        };
      });

      parentValues?.forEach((parentValue) => {
        const [key, value] = Object.entries(parentValue)[0];
        const tabView = tabsViewsMap.get(key);
        const cellData = tabsViewsMap.get(cellClicked?.column);
        const insertValue =
          cellData?.hierarchyOrder === tabView?.hierarchyOrder ||
          cellData?.hierarchyOrder > tabView?.hierarchyOrder;
        const needInsertValue = index === 0 && value && insertValue;

        info[key] = {
          ...defaultCellStyle,
          uniqueId: needInsertValue ? cellClicked?.uniqueId : undefined,
          value: needInsertValue ? originalRow[tabView.column] : null,
          tooltipType: needInsertValue ? value : null,
        };
      });

      rowsParsed.push({
        ...row,
        ...info,
      });
    });

    return rowsParsed;
  }

  return rows;
};

const getUpdatedItems = (
  tabsViews: TabView[],
  items: Item[],
  defaultCellStyle: { [key: string]: any },
  row?: { [key: string]: string | number },
): Item[] => {
  if (tabsViews?.length > 0) {
    const itemsParsed = [] as Item[];

    items?.reduce((accumulator, item, index) => {
      const info: Info = {};

      tabsViews.forEach((tabView) => {
        const cellInfo = row ? row?.[tabView.column] : null;
        const cellItemInfo =
          index !== 0 &&
          accumulator[tabView.column]?.value === item[tabView.column]?.value
            ? {}
            : item[tabView.column];

        info[tabView.column] = {
          value: index === 0 ? cellInfo : null,
          tooltipType: index === 0 ? cellInfo : null,
          ...defaultCellStyle,
          ...cellItemInfo,
        };
      });

      itemsParsed.push({
        ...item,
        ...info,
      });

      return item;
    }, {});

    return itemsParsed;
  }
  return items;
};

const TabsViewsContext = createContext<TabsViewsContextType | undefined>(
  undefined,
);

const TabsViewsProvider: React.FC<TabsViewsProviderProps> = ({
  children,
  value,
}) => {
  const [contentOriginal, setContentOriginal] = useState(value);
  const [contentTabs, setContentTabs] = useState(value);
  const { colors } = useTheme();
  const [activeTabs, setActiveTabs] = useState<Map<string, TabView>>(new Map());
  const [loading, setLoading] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [subPageFetching, setSubPageFetching] = useState(false);
  const [subPageFetchingId, setSubPageFetchingId] = useState<string | null>(null);
  const [mainTabPage, setMainTabPage] = useState(1);
  const [mainTabFetching, setMainTabFetching] = useState(false);
  const [mainTabHasMore, setMainTabHasMore] = useState(true);
  const [customState, setCustomStart] = useState<ContentTabsProps | null>(null);
  const globalFilters = useMemo(() => value?.filters, [value?.filters]);
  const [customStartFilters, setCustomStartFilters] = useState<string | null>(
    null,
  );

  const tabsViews = useMemo(() => {
    return (contentTabs?.table_views as TabView[]) || ([] as TabView[]);
  }, [contentTabs?.table_views]);

  const tabsViewsMap = useMemo(() => {
    const map = new Map<string, TabView>();
    tabsViews.forEach((tabView) => {
      map.set(tabView.column, tabView);
    });
    return map;
  }, [tabsViews]);

  const hasTabs = useMemo(() => tabsViews?.length > 0, [tabsViews]);
  const hasActiveTabs = useMemo(() => activeTabs?.size > 0, [activeTabs]);

  useEffect(() => {
    const defaultTabs = tabsViews?.filter((tabView) => tabView.default);
    const initialActiveTabs = new Map<string, TabView>();

    defaultTabs?.forEach((tabView) => {
      initialActiveTabs.set(tabView.column, {
        ...tabView,
        hasValue: true,
        selected: [],
      });
    });
    setActiveTabs(initialActiveTabs);
  }, [tabsViews]);

  const getActiveTabsAsFilters = useCallback(() => {
    const selected = [
      ...Array.from(activeTabs.values()).map((tab) => tab?.column),
    ];

    const columnTableFilter =
      Array.from(activeTabs.values())[0]?.table_filter || '';

    const defaultTab = tabsViews.find((tab) => tab.default)?.column;

    if (defaultTab && isEmpty(selected)) {
      selected.push(defaultTab);
    }

    const filterString = serializeFiltersTabs({
      table: columnTableFilter,
      filters: [
        {
          column: 'table_view_select',
          selected,
          type: FilterType.MULTI,
        },
      ],
    });

    return filterString;
  }, [activeTabs, tabsViews]);

  const getData = useCallback(
    async (
      url,
      field_filter,
      field_filter_compare,
      table_filter,
      filters,
      cancelToken,
      selectItem,
      row,
      page = 1,
    ): Promise<void> => {
      if (!url) {
        FeedbackError({ mainText: 'URL não informada' });
        return;
      }

      const activeTab = orderBy(
        Array.from(activeTabs.values()) || [],
        'hierarchyOrder',
        'asc',
      ).map((tab) => tab)[0];

      const filtersSerialized = serializeFiltersTabs({
        table: table_filter,
        filters: [
          {
            column: 'table_view_select',
            selected: [
              tabsViews.find((tab) => tab.default)?.column,
              ...Array.from(activeTabs.values()).map((tab) => tab?.column),
            ],
            type: FilterType.MULTI,
          },

          ...filters,
        ],
      });

      const data = await fetchCardData(
        url,
        {
          filters: filtersSerialized + globalFilters || globalFilters,
          page: `${page}`,
        },
        cancelToken,
      );

      if (!data || !data.content) return;

      const updatedRows = updateRows(
        data.content?.parentHierarchy,
        tabsViews,
        data?.content?.items,
        contentOriginal?.defaultCellStyle,
        tabsViewsMap,
        selectItem,
        row,
      );

      // Null out the clicked column for expanded rows (index > 0)
      // to avoid "multiplication" of parent values in drill-down
      const clickedCol = selectItem?.column;
      if (clickedCol) {
        updatedRows.forEach((r: any, i: number) => {
          if (i > 0 && r[clickedCol]) {
            r[clickedCol] = {
              ...r[clickedCol],
              value: null,
              tooltipType: null,
              uniqueId: undefined,
            };
          }
        });
      }

      const newGrouped = createGroupedHeaderAfterHeader({
        columns: [
          activeTab?.column_info,
          ...data?.content?.columns.filter((column) => column.type === 'group'),
        ] as any[],
        targetKey: tabsViews?.find(
          (tabView) => tabView?.column === activeTab.column,
        )?.column_info?.key as string,
        newColumns: tabsViews?.filter(
          (tabView) => tabView?.column !== activeTab.column,
        ),
        tableViews: tabsViews,
      });

      // Generate a unique ID for this expansion so the UI can identify
      // which specific row is expanded (needed when uniqueId from API is empty)
      const expandId = `${selectItem?.value}_${Date.now()}`;
      if (updatedRows[0]?.[selectItem?.column]) {
        updatedRows[0][selectItem.column] = {
          ...updatedRows[0][selectItem.column],
          uniqueId: expandId,
        };
      }

      // Propagate parent expansion uniqueIds to row[0] so that ancestor
      // tab columns (e.g. REDE when expanding BANDEIRA→LOJA) correctly
      // show the "-" indicator instead of "+".
      // We read the raw contentTabs row (before it's replaced) to get the
      // uniqueIds that were previously stamped by ancestor expansions.
      const rawExpandedRow = getOriginalRow(
        contentTabs?.items,
        selectItem.column,
        selectItem.value,
      )?.[0];
      if (rawExpandedRow && updatedRows[0]) {
        const selHierarchyOrder =
          tabsViewsMap.get(selectItem.column)?.hierarchyOrder ?? Infinity;
        tabsViews.forEach((tabView) => {
          if (
            tabView.hierarchyOrder < selHierarchyOrder &&
            (rawExpandedRow as any)[tabView.column]?.uniqueId &&
            updatedRows[0][tabView.column]
          ) {
            updatedRows[0][tabView.column] = {
              ...updatedRows[0][tabView.column],
              uniqueId: (rawExpandedRow as any)[tabView.column].uniqueId,
            };
          }
        });
      }

      const itemsCount = data?.content?.items?.length ?? 0;
      const itemsPerPage = data?.content?.items_per_page ?? itemsCount;
      const isPaginated =
        data?.content?.isPaginate &&
        itemsCount > 0 &&
        itemsCount >= itemsPerPage;

      const selected: SelectedItem = {
        ...selectItem,
        expandId,
        items: updatedRows,
        for_column: orderBy(
          Array.from(activeTabs.values()) || [],
          'hierarchyOrder',
          'asc',
        ).find(
          (tab) =>
            tab.hierarchyOrder >
            tabsViewsMap.get(selectItem?.column)?.hierarchyOrder,
        )?.column,
        originalRow: getOriginalRow(
          contentTabs?.items,
          selectItem.column,
          selectItem.value,
        ),
        row,
        url,
        page,
        hasMore: isPaginated,
        table_filter,
        filters,
        selectItem: selectItem,
        rowData: row,
      };

      const sentinelColumnLabel =
        tabsViews.find((t) => t.column === selectItem.column)?.label ||
        selectItem.column;
      const sentinelRow =
        isPaginated && !isEmpty(updatedRows)
          ? {
              __loadMoreSentinel: {
                expandId,
                columnLabel: sentinelColumnLabel,
                value: selectItem.value,
              },
            }
          : null;
      const rowsToInsert = sentinelRow
        ? [...updatedRows, sentinelRow]
        : updatedRows;

      setActiveTabs((prev) => {
        const newSelectedItems = new Map(prev);

        const prevSelected =
          newSelectedItems.get(selectItem.column)?.selected || [];

        newSelectedItems.set(selectItem.column, {
          ...newSelectedItems.get(selectItem.column),
          column: selectItem.column,
          selected: [...prevSelected, selected],
        });

        return newSelectedItems;
      });

      const updatedColumns =
        tabsViews?.length > 0 ? newGrouped : contentOriginal?.columns;

      setContentTabs((prev) => {
        let updatedItems = prev.items as any[];

        updatedItems = insertRowsAfter(
          updatedItems,
          selected.column,
          rowsToInsert,
          selected.value,
          selected.uniqueId,
        );

        return {
          ...prev,
          items: updatedItems,
          finalColumns: updatedColumns,
          footer: data?.content?.footer,
        };
      });

      setLoading(false);
    },
    [
      tabsViews,
      activeTabs,
      globalFilters,
      contentOriginal?.defaultCellStyle,
      contentOriginal?.columns,
      tabsViewsMap,
      contentTabs?.items,
    ],
  );

  const fetchNextSubPage = useCallback(
    async (targetExpandId: string) => {
      if (subPageFetching) return;

      let targetSelected: SelectedItem | null = null;
      let targetColumn: string | null = null;

      Array.from(activeTabs.entries()).forEach(([column, tab]) => {
        tab.selected?.forEach((sel) => {
          if (sel.expandId === targetExpandId) {
            targetSelected = sel;
            targetColumn = column;
          }
        });
      });

      if (!targetSelected || !targetColumn) return;

      const sel = targetSelected as SelectedItem;
      const nextPage = (sel.page || 1) + 1;

      setSubPageFetching(true);
      setSubPageFetchingId(targetExpandId);

      try {
        const defaultColumn = tabsViews.find((tab) => tab.default)?.column;
        const selectedColumns = [
          ...(defaultColumn ? [defaultColumn] : []),
          ...Array.from(activeTabs.values()).map((tab) => tab?.column),
        ].filter(Boolean) as string[];

        const filtersSerialized = serializeFiltersTabs({
          table: sel.table_filter || '',
          filters: [
            {
              column: 'table_view_select',
              selected: selectedColumns,
              type: FilterType.MULTI,
            },
            ...(sel.filters || []),
          ],
        });

        const data = await fetchCardData(
          sel.url,
          {
            filters: filtersSerialized + globalFilters || globalFilters,
            page: `${nextPage}`,
          },
          undefined as any,
        );

        const subItemsCount = data?.content?.items?.length ?? 0;
        const subItemsPerPage = data?.content?.items_per_page ?? subItemsCount;
        const isPaginated =
          data?.content?.isPaginate &&
          subItemsCount > 0 &&
          subItemsCount >= subItemsPerPage;

        const newRows =
          data?.content?.items
            ? updateRows(
                [] as any,
                tabsViews,
                data.content.items as any,
                contentOriginal?.defaultCellStyle,
                tabsViewsMap,
                sel.selectItem || sel,
                sel.rowData || sel.row,
              )
            : [];

        // Null out all tab columns at or above the expansion level so that
        // continuation rows from page 2+ don't show parent column values
        const selHierarchyOrder =
          tabsViewsMap.get(sel.column)?.hierarchyOrder ?? Infinity;
        newRows.forEach((row: any) => {
          tabsViews.forEach((tabView) => {
            if (
              tabView.hierarchyOrder <= selHierarchyOrder &&
              row[tabView.column]
            ) {
              row[tabView.column] = {
                ...row[tabView.column],
                value: null,
                tooltipType: null,
                uniqueId: undefined,
              };
            }
          });
        });

        setActiveTabs((prev) => {
          const newTabs = new Map(prev);
          const tab = newTabs.get(targetColumn!);
          if (tab?.selected) {
            tab.selected = tab.selected.map((s) =>
              s.expandId === targetExpandId
                ? {
                    ...s,
                    items: [...(s.items as any[]), ...newRows],
                    page: nextPage,
                    hasMore: isPaginated,
                  }
                : s,
            );
            newTabs.set(targetColumn!, { ...tab });
          }
          return newTabs;
        });

        setContentTabs((prev) => {
          const items = [...(prev.items as any[])];
          const sentinelIndex = items.findIndex(
            (row) => row?.__loadMoreSentinel?.expandId === targetExpandId,
          );

          if (sentinelIndex !== -1) {
            const oldSentinel = items[sentinelIndex]?.__loadMoreSentinel;
            const newSentinel = isPaginated
              ? {
                  __loadMoreSentinel: {
                    expandId: targetExpandId,
                    columnLabel: oldSentinel?.columnLabel,
                    value: oldSentinel?.value,
                  },
                }
              : null;
            items.splice(
              sentinelIndex,
              1,
              ...newRows,
              ...(newSentinel ? [newSentinel] : []),
            );
          }

          return { ...prev, items };
        });
      } finally {
        setSubPageFetching(false);
        setSubPageFetchingId(null);
      }
    },
    [
      subPageFetching,
      activeTabs,
      tabsViews,
      globalFilters,
      contentOriginal?.defaultCellStyle,
      tabsViewsMap,
    ],
  );

  const getDataCustom = useCallback(
    async (
      url,
      field_filter,
      field_filter_compare,
      table_filter,
      filters,
      cancelToken,
      selectItem,
      row,
      page = 1,
    ): Promise<void> => {
      if (!url) {
        FeedbackError({ mainText: 'URL não informada' });
        return;
      }

      const activeTab = orderBy(
        Array.from(activeTabs.values()) || [],
        'hierarchyOrder',
        'asc',
      ).map((tab) => tab?.column)[0];
      const active = orderBy(
        Array.from(activeTabs.values()) || [],
        'hierarchyOrder',
        'asc',
      ).map((tab) => tab)[0];

      const filtersSerialized = serializeFiltersTabs({
        table: table_filter,
        filters: [
          {
            column: 'table_view_select',
            selected: [activeTab],
            type: FilterType.MULTI,
          },

          ...filters,
        ],
      });

      const data = await fetchCardData(
        url,
        {
          filters: filtersSerialized + globalFilters || globalFilters,
          page: `${page}`,
        },
        cancelToken,
      );

      if (!data || !data.content) return;

      if (page <= 1) {
        setCustomStart(data);
      }

      const updatedRows = updateRows(
        page > 1 ? [] : data.content?.parentHierarchy,
        tabsViews,
        data?.content?.items,
        contentOriginal?.defaultCellStyle,
        tabsViewsMap,
        selectItem,
        row,
      );

      // For page > 1, just append items without resetting everything
      if (page > 1) {
        setContentTabs((prev) => ({
          ...prev,
          items: [...((prev.items as any[]) || []), ...updatedRows],
        }));
        setMainTabPage(page);
        setMainTabHasMore((data.content?.items?.length || 0) >= 50);
        setMainTabFetching(false);
        return;
      }

      const selected: SelectedItem = {
        ...selectItem,
        items: updatedRows,
        row,
        url,
      };

      setActiveTabs((prev) => {
        const newSelectedItems = new Map(prev);

        const prevSelected =
          newSelectedItems.get(selectItem.column)?.selected || [];

        // Set hasValue: false for all other tabs
        newSelectedItems.forEach((value, key) => {
          if (key !== selectItem.column) {
            newSelectedItems.set(key, {
              ...value,
              selected: [],
              hasValue: false,
            });
          }
        });

        // Update the selected tab
        newSelectedItems.set(selectItem.column, {
          ...newSelectedItems.get(selectItem.column),
          column: selectItem.column,
          selected: [...prevSelected, selected],
          hasValue: true,
        });

        return newSelectedItems;
      });

      const newGrouped = createGroupedHeaderAfterHeader({
        columns: data?.content?.columns as any[],
        targetKey: tabsViews?.find((tabView) => tabView?.column === activeTab)
          ?.column_info?.key as string,
        newColumns: tabsViews?.filter(
          (tabView) => tabView?.column !== activeTab,
        ),
        tableViews: tabsViews,
      });

      const updatedColumns =
        tabsViews?.length > 0 ? newGrouped : contentOriginal?.columns;

      setContentTabs((prev) => {
        return {
          ...prev,
          columns: updatedColumns,
          items: selected.items,
          finalColumns: null,
          footer: data?.content?.footer,
        };
      });

      // Track pagination state for main tab level
      setMainTabPage(1);
      setMainTabHasMore((data.content?.items?.length || 0) >= 50);

      setGlobalLoading(false);
      setLoading(false);
    },
    [activeTabs, globalFilters, tabsViews, contentOriginal, tabsViewsMap],
  );

  // Main tab-level pagination: reuses getDataCustom with page > 1
  const fetchNextMainTabPage = useCallback(async () => {
    if (mainTabFetching || !mainTabHasMore) return;

    setMainTabFetching(true);

    const [selectItem] = orderBy(
      Array.from(activeTabs.values()) || [],
      'hierarchyOrder',
      'asc',
    );

    if (!selectItem) {
      setMainTabFetching(false);
      return;
    }

    const { baseURL, table_filter } = selectItem;

    await getDataCustom(
      baseURL,
      '',
      '',
      table_filter,
      [],
      null,
      selectItem,
      {},
      mainTabPage + 1,
    );
  }, [mainTabFetching, mainTabHasMore, mainTabPage, activeTabs, getDataCustom]);

  useEffect(() => {
    if (activeTabs.size === 0) {
      setCustomStart(null);
      setGlobalLoading(false);
    }

    if (activeTabs.size > 0 && globalFilters !== customStartFilters) {
      setGlobalLoading(true);
      setCustomStart(null);
      setCustomStartFilters(globalFilters);

      setActiveTabs((prev) => {
        const newActiveTabs = new Map(prev);

        newActiveTabs.forEach((tab, key) => {
          newActiveTabs.set(key, {
            ...tab,
            selected: [],
          });
        });
        return newActiveTabs;
      });

      const [selectItem] = orderBy(
        Array.from(activeTabs.values()) || [],
        'hierarchyOrder',
        'asc',
      );
      const { baseURL, table_filter } = selectItem;

      getDataCustom(baseURL, '', '', table_filter, [], null, selectItem, {});
    }

    if (activeTabs.size > 1 && !customState) {
      const selectItemOld = orderBy(
        Array.from(activeTabs.values()) || [],
        'hierarchyOrder',
        'asc',
      )?.find((tab) => tab?.hasValue) || { hierarchyOrder: Infinity };

      const selectItem = orderBy(
        Array.from(activeTabs.values()) || [],
        'hierarchyOrder',
        'asc',
      )?.find((tab) => tab?.hierarchyOrder < selectItemOld?.hierarchyOrder);

      if (selectItem) {
        setGlobalLoading(true);
        setCustomStart(null);
        const { baseURL, table_filter } = selectItem;

        getDataCustom(baseURL, '', '', table_filter, [], null, selectItem, {});
      }
    }

    if (activeTabs.size === 1 && !customState) {
      setGlobalLoading(true);
      setCustomStart(null);
      const [selectItem] = orderBy(
        Array.from(activeTabs.values()) || [],
        'hierarchyOrder',
        'asc',
      );
      const { baseURL, table_filter } = selectItem;

      getDataCustom(baseURL, '', '', table_filter, [], null, selectItem, {});
    }
  }, [
    activeTabs,
    contentTabs?.filters,
    customStartFilters,
    customState,
    getDataCustom,
    globalFilters,
  ]);

  const handleSelectedItems = useCallback(
    ({ data, checked }) => {
      if (checked) {
        setContentTabs((prev) => {
          const oldValues: SelectedItem =
            activeTabs
              .get(data?.column?.id)
              ?.selected?.find((item) => {
                if (item.value !== data.value) return false;
                if (item?.expandId)
                  return item.expandId === data?.uniqueId;
                if (item?.uniqueId && data?.uniqueId)
                  return item.uniqueId === data.uniqueId;
                return true;
              }) ||
            ({
              items: [] as Array<unknown>,
              originalRow: [] as Array<unknown>,
            } as SelectedItem);

          const updatedItems = removeRowsByKeyAfter(prev?.items, oldValues);

          return {
            ...prev,
            items: updatedItems,
            finalColumns: null,
          };
        });

        setActiveTabs((prev) => {
          const newSelectedItems = new Map(prev);

          newSelectedItems.forEach((tab) => {
            const tabHiearchyOrder = tabsViewsMap.get(tab?.column)
              ?.hierarchyOrder;
            const dataHiearchyOrder = tabsViewsMap.get(data?.column?.id)
              ?.hierarchyOrder;

            const hierarchyOrderCheck = tabHiearchyOrder >= dataHiearchyOrder;
            const hierarchyOrderEqual = tabHiearchyOrder === dataHiearchyOrder;

            if (
              tab?.selected &&
              tab?.selected?.length > 0 &&
              hierarchyOrderCheck
            ) {
              tab.selected = hierarchyOrderEqual
                ? tab.selected.filter((element) => {
                    if (element.value !== data.value) return true;
                    if (element?.expandId)
                      return element.expandId !== data?.uniqueId;
                    if (element?.uniqueId && data?.uniqueId)
                      return element.uniqueId !== data.uniqueId;
                    return false;
                  })
                : [];
            }
          });

          return newSelectedItems;
        });
      } else {
        setLoading(true);
        getData(
          data?.dataSubcomponent?.data?.url,
          data?.dataSubcomponent?.data?.field_filter,
          data?.dataSubcomponent?.data?.field_filter_compare,
          data?.dataSubcomponent?.data?.table_filter,
          [
            {
              column: data.column?.id,
              key: 'table_view_filters',
              selected: [data?.value],
              type: FilterType.MULTI,
            },
          ],
          // [
          //   {
          //     column: data.column?.id,
          //     key: 'table_view_filters',
          //     selected: [data?.uniqueId],
          //     type: FilterType.MULTI,
          //   },
          // ],
          null,
          {
            ...data,
            column: data.column?.id,
            value: data?.value,
          },
          data?.row?.original,
        );
      }
    },
    [activeTabs, tabsViewsMap, getData],
  );

  useEffect(() => {
    if (activeTabs.size === 0 && !hasActiveTabs) {
      setContentTabs(contentOriginal);
    }
  }, [contentOriginal, activeTabs, setContentTabs, hasActiveTabs]);

  const resetInfo = useCallback(() => {
    const updatedColumns =
      tabsViews?.length > 0
        ? createGroupedHeaderAfterHeader({
            columns: contentOriginal?.columns as any[],
            targetKey: tabsViews?.find((tabView) => tabView.default)
              ?.column_info?.key as string,
            newColumns: tabsViews?.filter((tabView) => !tabView.default),
          })
        : contentOriginal?.columns;

    setContentTabs((prev) => ({
      ...prev,
      ...contentOriginal,
      columns: updatedColumns,
      items: getUpdatedItems(
        tabsViews,
        contentOriginal?.items,
        contentOriginal?.defaultCellStyle,
      ),
    }));
  }, [contentOriginal, tabsViews]);

  useEffect(() => {
    resetInfo();
  }, []);

  return (
    <TabsViewsContext.Provider
      value={{
        contentTabs,
        colors,
        tabsViews,
        tabsViewsMap,
        hasTabs,
        hasActiveTabs,
        activeTabs,
        loading,
        contentOriginal,
        globalLoading,
        setGlobalLoading,
        setContentOriginal,
        setActiveTabs,
        setContentTabs,
        handleSelectedItems,
        resetInfo,
        setCustomStart,
        getActiveTabsAsFilters,
        fetchNextSubPage,
        subPageFetching,
        subPageFetchingId,
        fetchNextMainTabPage,
        mainTabFetching,
        mainTabHasMore,
      }}
    >
      {children}
    </TabsViewsContext.Provider>
  );
};

function useTabsViewsContext(): TabsViewsContextType {
  const context = useContext(TabsViewsContext);

  if (!context) {
    throw new Error(
      'useTabsViewsContext deve ser usado dentro de um componente fornecido com o TabsViewsContext.Provider',
    );
  }

  return context;
}

const TabsViews: React.FC = () => {
  const {
    colors,
    tabsViews,
    activeTabs,
    setActiveTabs,
    setCustomStart,
    setContentTabs,
  } = useTabsViewsContext();
  return (
    <Flex gap="0.25rem" marginBottom="0.50rem">
      {tabsViews?.map((view) => {
        const checked = activeTabs.has(view.column);

        return (
          <Button
            type="button"
            gap="0.25rem"
            variant={checked ? 'solid' : 'outline'}
            key={view.key}
            color={`${
              checked ? colors.primaryContent : colors.primary ?? 'white'
            }`}
            backgroundColor={
              checked ? colors.primary : colors.primaryContent ?? 'white'
            }
            cursor="pointer"
            onClick={() => {
              setActiveTabs((prev) => {
                const newActiveTabs = new Map(prev);

                const selectItemOld = Array.from(newActiveTabs.values())?.find(
                  (tab) => tab?.hasValue,
                );

                const selectItem = orderBy(
                  Array.from(newActiveTabs.values()) || [],
                  'hierarchyOrder',
                  'asc',
                )?.find((tab) => {
                  return tab?.hierarchyOrder < selectItemOld?.hierarchyOrder;
                });

                if (checked) {
                  newActiveTabs.forEach((tab, key) => {
                    if (
                      tab?.selected &&
                      tab?.selected?.length > 0 &&
                      tab.selected.some((tab) => tab.for_column === view.column)
                    ) {
                      newActiveTabs.set(key, {
                        ...tab,
                        selected: [],
                      });
                    }
                  });
                  newActiveTabs.delete(view.column);
                } else {
                  const hasBiggerHierarchy =
                    Array.from(newActiveTabs.values()).filter(
                      (tab) => tab.hierarchyOrder > view.hierarchyOrder,
                    ).length > 0;

                  newActiveTabs.forEach((tab, key) => {
                    if (hasBiggerHierarchy) {
                      newActiveTabs.set(key, {
                        ...tab,
                        selected: [],
                      });
                    }
                  });
                  newActiveTabs.set(view.column, {
                    ...view,
                    selected: [],
                  });
                }

                if (newActiveTabs.size >= 1) {
                  if (selectItem || !selectItemOld || !selectItem) {
                    setCustomStart(null);
                  }
                }

                return newActiveTabs;
              });

              setContentTabs((prev) => {
                const hasBiggerHierarchy =
                  Array.from(activeTabs.values()).filter(
                    (tab) => tab.hierarchyOrder > view.hierarchyOrder,
                  ).length > 0;

                if (hasBiggerHierarchy && !checked) {
                  let updatedItems = prev.items;

                  activeTabs.forEach((tab, key) => {
                    if (tab?.hierarchyOrder <= view.hierarchyOrder) {
                      tab?.selected?.forEach((item) => {
                        if (item?.originalRow && item?.originalRow.length > 0) {
                          updatedItems = removeRowsByKeyAfter(
                            updatedItems,
                            item,
                          );
                        }
                      });
                    }
                  });
                  return {
                    ...prev,
                    items: updatedItems,
                    finalColumns: null,
                  };
                }

                if (checked) {
                  let updatedItems = prev.items;
                  activeTabs.forEach((tab, key) => {
                    if (tab?.selected && tab?.selected?.length > 0) {
                      tab?.selected?.forEach((item) => {
                        if (
                          item?.originalRow &&
                          item?.originalRow.length > 0 &&
                          item.for_column === view.column
                        ) {
                          updatedItems = removeRowsByKeyAfter(
                            updatedItems,
                            item,
                          );
                        }
                      });
                    }
                  });

                  return {
                    ...prev,
                    items: updatedItems,
                    finalColumns: null,
                  };
                }

                return prev;
              });
            }}
          >
            {view.label}
          </Button>
        );
      })}
    </Flex>
  );
};

export { TabsViewsProvider, useTabsViewsContext, TabsViews };
